import React, { useState } from 'react';
import { Box, Typography, Button, IconButton, Dialog } from '@mui/material';
import { Visibility, VisibilityOff, Close } from '@material-ui/icons';
import useStyles from '../../../../assets/styles/DQProV2SignupStyles';
import PoweredByDQ_Default from '../../../../assets/images/PoweredByDQ_Default.png';
import PoweredByDQ_White from '../../../../assets/images/PoweredByDQ_white.png';
import { useColorMode } from '../../../../context/colorMode/colorMode.context';
import { registerWithEmailService, loginUser, getGoogleSignInLink, persistGuestRegisterUser, persistGuestGetGoogleSignInLink } from '../../../../services/auth.service';
import { useAuth } from '../../../../context/auth';
import { save_e2ee_keys } from '../../../../utils/utils';
import { LOCAL_STORAGE_KEYS } from '../../../../utils/constants';
import LoaderWithBackDrop from '../../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp';
import PasswordWithValidation, { getPasswordChecks } from './PasswordWithValidationsField.comp';

const SignupDialogV2 = ({ open, onClose, onLoginClick, onSuccess }) => {
    const classes = useStyles();
    const { isDark } = useColorMode();
    const PoweredByDQ_Logo = isDark ? PoweredByDQ_White : PoweredByDQ_Default;
    const { setAuthToken, setAuthUser, authUser } = useAuth();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({ email: '', password: '', confirmPassword: '' });

    const handleSignupSuccess = (result) => {
        if (result.data && result.data.user) {
            setAuthToken(result.data.auth_token);
            setAuthUser(result.data.user);
            localStorage.setItem(LOCAL_STORAGE_KEYS.refresh_token, JSON.stringify(result.data.refresh_token));
            localStorage.setItem(LOCAL_STORAGE_KEYS.user_email, JSON.stringify(result.data.user.email));
            if (result.data.e2ee_pub_key) save_e2ee_keys(result);
            if (onSuccess) onSuccess();
            onClose();
        }
    };

    const loginWithGoogleAction = async () => {
        setLoading(true);
        try {
            const result = authUser?.is_guest ? await persistGuestGetGoogleSignInLink() : await getGoogleSignInLink();
            localStorage.setItem(LOCAL_STORAGE_KEYS.dqpro_v2_google_auth_redirect, 'true');
            window.open(result.data.redirect_link, '_self');
        } catch (e) { setLoading(false); }
    };

    const handleContinue = async () => {
        let newErrors = { email: '', password: '', confirmPassword: '' };
        let hasError = false;
        if (!email.trim()) { newErrors.email = 'Please enter your email address'; hasError = true; }
        if (!password.trim()) { newErrors.password = 'Please enter a password'; hasError = true; }
        else {
            const isPasswordValid = Object.values(getPasswordChecks(password)).every(Boolean);
            if (!isPasswordValid) { newErrors.password = 'Weak password'; hasError = true; }
        }
        if (password !== confirmPassword) { newErrors.confirmPassword = "Passwords don't match"; hasError = true; }
        setErrors(newErrors);
        if (hasError) return;

        try {
            setLoading(true);
            if (authUser?.is_guest) {
                const result = await persistGuestRegisterUser(email.trim(), password);
                handleSignupSuccess(result);
            } else {
                const registerResult = await registerWithEmailService(email.trim(), password);
                if (registerResult.data?.auth_token) {
                    handleSignupSuccess(registerResult);
                } else {
                    const loginResult = await loginUser(email.trim(), password, false);
                    handleSignupSuccess(loginResult);
                }
            }
        } catch (e) {} finally { setLoading(false); }
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: '24px',
                    padding: '20px 0px',
                    maxWidth: '480px',
                    overflowX: 'hidden',
                    backgroundColor: 'var(--brand-surface, #fff)'
                }
            }}
        >
            <IconButton onClick={onClose} style={{ position: 'absolute', right: 10, top: 10, color: 'var(--brand-text-muted, #999)' }}>
                <Close />
            </IconButton>
            <Box style={{ padding: '0 40px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography className={classes.welcomeText} style={{ fontSize: '32px', marginBottom: '10px' }}>Welcome to</Typography>
                <img src={PoweredByDQ_Logo} alt="Powered by DQ" className={classes.logo} style={{ height: '70px', marginBottom: '25px' }} />

                {process.env.REACT_APP_ENABLE_LOGIN_WITH_GOOGLE === '1' && (
                    <Button className={classes.googleButton} onClick={loginWithGoogleAction} disabled={loading}>
                        <img src="https://www.gstatic.com/images/branding/product/1x/gsa_512dp.png" alt="Google Icon" style={{ width: '20px', height: '20px' }} />
                        Sign up with Google
                    </Button>
                )}

                <Box className={classes.dividerContainer}>
                    <Box className={classes.dividerLine}></Box>
                    <Box className={classes.dividerText}>OR</Box>
                    <Box className={classes.dividerLine}></Box>
                </Box>

                <form className={classes.form} onSubmit={(e) => { e.preventDefault(); handleContinue(); }}>
                    <Typography className={classes.label}>Email</Typography>
                    <input className={classes.input} type="email" placeholder="Enter your email address" value={email} onChange={(e) => setEmail(e.target.value)} disabled={loading} />
                    {errors.email && <Typography style={{ color: 'red', fontSize: '12px', marginTop: '-10px', marginBottom: '10px' }}>{errors.email}</Typography>}

                    <Typography className={classes.label}>Password</Typography>
                    <PasswordWithValidation value={password} onChange={setPassword} placeholder="Enter your password" disabled={loading} inputClassName={classes.input} />
                    {errors.password && <Typography style={{ color: 'red', fontSize: '12px', marginTop: '-10px', marginBottom: '10px' }}>{errors.password}</Typography>}

                    <Typography className={classes.label}>Confirm Password</Typography>
                    <Box sx={{ position: 'relative', width: '100%' }}>
                        <input
                            className={classes.input}
                            type={showConfirmPassword ? 'text' : 'password'}
                            placeholder="Re-enter your password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            disabled={loading}
                            style={{ paddingRight: '42px' }}
                        />
                        <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            sx={{ position: 'absolute', right: 8, top: '40%', transform: 'translateY(-50%)' }}
                        >
                            {showConfirmPassword ? <VisibilityOff style={{ fontSize: 16 }} /> : <Visibility style={{ fontSize: 16 }} />}
                        </IconButton>
                    </Box>
                    {errors.confirmPassword && <Typography style={{ color: 'red', fontSize: '12px', marginTop: '-10px', marginBottom: '10px' }}>{errors.confirmPassword}</Typography>}

                    <Button className={classes.continueButton} type="submit" disabled={loading}>
                        {loading ? 'Please wait...' : 'Continue'}
                    </Button>
                </form>

                <Typography className={classes.signupText} style={{ marginBottom: '20px', fontSize: '16px' }}>
                    Already have an account? <span className={classes.signupLink} onClick={onLoginClick}>Log in</span>
                </Typography>
            </Box>
            <LoaderWithBackDrop loading={loading} />
        </Dialog>
    );
};

export default SignupDialogV2;
